/* eslint-disable no-console */ import { noop } from "@sail/utils";
import { useCallback, useMemo, useRef } from "react";
import { getApolloContext, useDataContext } from "../../../internal/revamp/apollo/operationContext";
import useGraphQlDataClient from "../../../internal/revamp/config/useGraphQlDataClient";
import addTypename from "../../../internal/revamp/queryUtils/addTypename";
import { reportRuntimeError } from "../../../internal/revamp/telemetry/internalReporting";
import { getOperationName } from "../../../internal/shared/utils/gql";
import useInvalidateTopLevelFields from "./useInvalidateTopLevelFields";
export default function useMutation(/**
   * The options to pass to the mutation.
   */ options) {
    const mutationDocument = options.doc;
    const transformedMutation = useMemo(()=>addTypename(mutationDocument), [
        mutationDocument
    ]);
    const { apolloLinkExecutor, memoryCache } = useGraphQlDataClient();
    const operationName = getOperationName(mutationDocument) || 'CouldNotFindMutationName';
    const expectedErrorsRef = useRef(options.expectedErrors);
    const invalidateQueries = useInvalidateTopLevelFields();
    const dataContext = useDataContext();
    return useCallback((callbackOptions)=>{
        const { variables } = callbackOptions;
        return new Promise((resolve)=>{
            const apolloLinkContext = getApolloContext(dataContext, {
                type: 'MUTATION',
                operationName,
                expectedErrors: expectedErrorsRef.current || []
            });
            try {
                apolloLinkExecutor.execute(transformedMutation, variables, apolloLinkContext, {
                    next: (result)=>{
                        if (result.data) {
                            memoryCache.writeMutation({
                                mutation: transformedMutation,
                                variables,
                                data: result.data
                            });
                            // Query invalidation on successful mutations when requested by consumer
                            if (typeof (options === null || options === void 0 ? void 0 : options.invalidateTopLevelFields) !== 'undefined' && options.invalidateTopLevelFields.length > 0) {
                                invalidateQueries(options.invalidateTopLevelFields);
                            }
                        }
                        resolve(result);
                    },
                    complete: noop
                });
            } catch (error) {
                reportRuntimeError(apolloLinkContext.observabilityFns, error);
                resolve({
                    data: null,
                    errors: {
                        graphQlErrors: [],
                        clientErrors: [
                            error
                        ],
                        networkErrors: []
                    }
                });
            }
        });
    }, [
        dataContext,
        operationName,
        apolloLinkExecutor,
        transformedMutation,
        options,
        invalidateQueries,
        memoryCache
    ]);
}

//# sourceMappingURL=useMutation.js.map